import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Table from '../ogComponent/table';
import { IconCheck, IconX } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import Spinner from '../ogComponent/spinner';
import debounce from 'lodash/debounce';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import "./attendence.css";

export default function RequestList() {
    const [selectedRequest, setSelectedRequest] = useState("Pending Requests");
    const [product, setProduct] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const accessToken = localStorage.getItem('accessToken');

    const fetchData = async (search = '') => {
        setLoading(true);
        try {
            const statusMap = {
                "Pending Requests": "Pending",
                "Approved Requests": "Approved",
                "Rejected Requests": "Rejected"
            };

            const status = statusMap[selectedRequest];
            const params = { status, search: search.trim() };

            if (startDate) params.start_date = startDate.format('YYYY-MM-DD');
            if (endDate) params.end_date = endDate.format('YYYY-MM-DD');

            const response = await axios.get(`${process.env.REACT_APP_API}/api/attendance/attendance-regularization/`, {
                params,
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });

            const formatTime = (time) => {
                if (!time) return 'N/A';
                if (typeof time !== 'string') return time;
                if (time.includes('AM') || time.includes('PM')) return time;
                return dayjs(`2000-01-01 ${time}`).format('hh:mm A');
            };

            if (response.data.success) {
                const formattedData = response.data.data.map((item, index) => ({
                    ...item,
                    SiNo: index + 1,
                    name: (
                        <div className="d-flex align-items-center gap-3">
                            <img src={item.employee?.profile_photo || '/assets/user.png'} className="att-cell-img" alt="user" />
                            <span className="att-name-text">{item.employee?.name || "N/A"}</span>
                        </div>
                    ),
                    date: dayjs(item.date).format('DD MMM YYYY'),
                    reason: <span className="text-muted small" title={item.reason}>{item.reason?.substring(0, 30)}{item.reason?.length > 30 ? '...' : ''}</span>,
                    punch_type: (
                        <span className={`badge border-0 ${item.punch_direction === 'CHECK_IN' ? 'bg-primary bg-opacity-10 text-primary' : 'bg-info bg-opacity-10 text-info'}`}>
                            {item.punch_direction === 'CHECK_IN' ? 'Punch-In' : 'Punch-Out'}
                        </span>
                    ),
                    punch_time: <span className="fw-bold">{formatTime(item.punch_time)}</span>,
                    actions: (
                        <div className="d-flex gap-2">
                            {(selectedRequest === "Pending Requests" || selectedRequest === "Rejected Requests") && (
                                <span className="action-icon edit-icon" title="Approve" onClick={(e) => { e.stopPropagation(); handleApproveReject(item.id, 'Approved'); }}>
                                    <IconCheck size={18} stroke={3} />
                                </span>
                            )}
                            {(selectedRequest === "Pending Requests" || selectedRequest === "Approved Requests") && (
                                <span className="action-icon delete-icon" title="Reject" onClick={(e) => { e.stopPropagation(); handleApproveReject(item.id, 'Rejected'); }}>
                                    <IconX size={18} stroke={3} />
                                </span>
                            )}
                        </div>
                    )
                }));
                setProduct(formattedData);
            } else {
                setProduct([]);
                setError('Failed to fetch data');
            }
        } catch (error) {
            setProduct([]);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const debouncedFetchData = useCallback(
        debounce((search) => { fetchData(search); }, 800),
        [selectedRequest, startDate, endDate, accessToken]
    );

    useEffect(() => {
        debouncedFetchData(searchQuery);
        return () => debouncedFetchData.cancel();
    }, [searchQuery, selectedRequest, startDate, endDate, debouncedFetchData]);

    const handleApproveReject = async (requestId, status) => {
        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_API}/api/attendance/attendance-regularization/${requestId}/approve-reject/`,
                { status },
                { headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' } }
            );

            if (response.data.success) {
                toast.success(`Request ${status.toLowerCase()}`);
                fetchData(searchQuery);
            } else {
                toast.error('Failed to process the request');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'An error occurred');
        }
    };

    const header = [
        { name: "SiNo", label: "#", active: true },
        { name: "name", label: "Employee", active: true },
        { name: "reason", label: "Reason", active: true },
        { name: "date", label: "Date", active: true },
        { name: "punch_type", label: "Direction", active: true },
        { name: "punch_time", label: "Requested Time", active: true },
        { name: "actions", label: "Actions", active: true },
    ];

    return (
        <div className="AttendanceContainer w-100 h-100 px-4 pt-4 pb-5">
            <div className="attendance-header mb-4">
                <h3 className="m-0">Punch Regularizations</h3>
                <p className="text-muted small mb-0">Manage and process manual attendance correction requests from employees.</p>
            </div>

            <div className="attendance-filters">
                <div className="d-flex flex-grow-1 gap-3">
                    <div className="dropdown">
                        <button className="btn filter-btn dropdown-toggle" type="button" data-bs-toggle="dropdown" style={{ minWidth: '180px' }}>
                            {selectedRequest}
                        </button>
                        <ul className="dropdown-menu">
                            {["Pending Requests", "Approved Requests", "Rejected Requests"].map(req => (
                                <li key={req} onClick={() => setSelectedRequest(req)}><span className="dropdown-item m-0">{req}</span></li>
                            ))}
                        </ul>
                    </div>

                    <div className="Tablesearch position-relative">
                        <input
                            className="form-control border-0 shadow-sm"
                            type="search"
                            placeholder="Search employee..."
                            style={{ height: '40px', width: '280px', backgroundColor: 'var(--bg-color)', paddingLeft: '16px' }}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <span style={{ position: "absolute", right: "15px", top: '50%', transform: 'translateY(-50%)' }}>
                            <i className="bi bi-search"></i>
                        </span>
                    </div>
                </div>

                <div className="date-picker-group">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker label="Start Date" format="DD/MM/YYYY" value={startDate} onChange={setStartDate} slotProps={{ textField: { size: 'small', sx: { width: 160 } } }} />
                        <DatePicker label="End Date" format="DD/MM/YYYY" value={endDate} onChange={setEndDate} slotProps={{ textField: { size: 'small', sx: { width: 160 } } }} />
                    </LocalizationProvider>
                </div>
            </div>

            {loading ? (
                <div className="py-5 text-center"><Spinner /></div>
            ) : (
                <div className="attendance-table-wrapper">
                    {product.length === 0 ? (
                        <div className="empty-attendance py-5">
                            <img src="/assets/file.png" className="empty-img" alt="No data" />
                            <p className="fw-semibold text-muted">No punch requests found currently.</p>
                        </div>
                    ) : (
                        <Table
                            header={header}
                            product={product}
                            onRowClick={() => { }}
                            loading={loading}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                        />
                    )}
                </div>
            )}
        </div>
    );
}